import { useState, type FormEvent } from 'react'
import type { NavSearch } from '@assessments/shared'
import { cn } from '@/lib/utils'
import { buildSearchUrl, canResolveSearchUrl } from '../../lib/navSearch.js'

interface Props {
  /** Search section from the nav feed. Undefined/incomplete → renders nothing. */
  search?: NavSearch
  /** Platform origin the destination must resolve to. Absent/off-origin → renders nothing. */
  platformOrigin?: string
  /** Fill the container (mobile menu) instead of the desktop clamp width — `.nav-search-bar--mobile`. */
  fullWidth?: boolean
}

const DEFAULT_PLACEHOLDER = 'Search'

// Magnifier matching the Platform global-nav search icon.
function SearchIcon() {
  return (
    <svg
      className="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  )
}

/**
 * Platform search bar for the global nav. Reads its destination and field name from the nav
 * feed at runtime and hands a submitted query off to the platform results page. Styled to match
 * the Platform `.nav-search-bar` (ethics-quotient navigation.scss): a compact bordered box
 * (input border, input radius) holding a 30px borderless input and a subtle→heading magnifier button.
 */
export function SearchBar({ search, platformOrigin, fullWidth = false }: Props) {
  const [query, setQuery] = useState('')

  // Render only with a complete search config AND a destination that resolves to the
  // platform origin (relative urls and same-origin absolute urls; off-origin rejected).
  if (!search?.url || !search.field || !canResolveSearchUrl(search.url, platformOrigin)) return null
  const config = search

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    window.location.assign(buildSearchUrl(platformOrigin, config, trimmed))
  }

  return (
    <form role="search" onSubmit={handleSubmit} className={cn(fullWidth && 'w-full')}>
      <div className="flex items-center overflow-hidden rounded-input border border-input bg-surface">
        <input
          type="search"
          aria-label="Search"
          placeholder={config.placeholder ?? DEFAULT_PLACEHOLDER}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className={cn(
            'h-[30px] border-none bg-transparent px-2 text-[13px] text-heading outline-none',
            fullWidth ? 'w-full flex-1' : 'w-[clamp(160px,42.553vw_-_275.744px,320px)]',
          )}
        />
        <button
          type="submit"
          aria-label="Submit search"
          className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-transparent text-subtle hover:text-heading"
        >
          <SearchIcon />
        </button>
      </div>
    </form>
  )
}
